import { ProjectWithRelations } from "@shared/schema";
import { getActiveStatus } from "@/lib/budget-utils";
import { Button } from "@/components/ui/button";
import { ChevronDown, Edit, Mail, Plus, MessageCircle, Tag as TagIcon, Paperclip, Settings, Upload, Lock, Unlock } from "lucide-react";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { EditableStatus } from "./editable-status";
import { TaggableDateCell } from "./taggable-date-cell";
import { ProjectEditDialog } from "./project-edit-dialog";
import { ProjectDetailDialog } from "./project-detail-dialog";
import { PublicModeConfigDialog } from "./public-mode-config-dialog";
import { TagManager } from "./tag-manager";
import { FileBrowser } from "./file-browser";
import { ThumbnailUploadDialog } from "./thumbnail-upload-dialog";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useState, useRef, useCallback, useMemo } from "react";
import { logger } from "@/services/debug-logger";
import { TREE_COLUMNS, TREE_GRID_TEMPLATE, TREE_GRID_TEMPLATE_WITH_BUDGET, getTreeColumnStyles } from "@/lib/tree-columns";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { useStatusConfigs } from "@/hooks/use-shared-queries";
import type { StatusConfig } from "@shared/schema";
import { getUserColor } from '@/lib/user-color';
import { ProjectIdCopyMenu } from "./project-id-copy-menu";
import { TimeTrackButton } from "@/components/timetrack";

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
function blendHex(tint: string | undefined, base: string, amt: number): string {
  if (!tint || !HEX_RE.test(tint)) return base;
  const expand = (h: string) => h.length === 4
    ? '#' + h[1] + h[1] + h[2] + h[2] + h[3] + h[3]
    : h;
  const t = expand(tint), b = expand(base);
  const tr = parseInt(t.slice(1, 3), 16), tg = parseInt(t.slice(3, 5), 16), tb = parseInt(t.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16), bg = parseInt(b.slice(3, 5), 16), bb = parseInt(b.slice(5, 7), 16);
  const r = Math.round(tr * amt + br * (1 - amt));
  const g = Math.round(tg * amt + bg * (1 - amt));
  const b2 = Math.round(tb * amt + bb * (1 - amt));
  return `rgb(${r}, ${g}, ${b2})`;
}

interface ProjectRowProps {
  /** pulseCount is a display projection, never a replacement for stored counts. */
  project: ProjectWithRelations & { pulseCount?: number };
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onOpenChat?: (projectId: string) => void;
  onDropFiles?: (projectId: string, files: File[]) => void;
  showBudgetColumn?: boolean;
  budgetItems?: any[];
  adminPulseVisibility?: string;
}


export function ProjectRow({
  project,
  isExpanded,
  onToggleExpanded,
  onOpenChat,
  onDropFiles,
  showBudgetColumn = false,
  budgetItems,
  adminPulseVisibility
}: ProjectRowProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const isDeactivated = project.lifecycleState === 'deactivated';
  const isArchived = project.lifecycleState === 'archived';
  const isReadOnly = isDeactivated || isArchived;
  const isAdmin = user?.role === 'admin';

  const canUpload = useMemo(() => {
    if (!user || !onDropFiles) return false;
    if (isReadOnly) return false;
    if (isAdmin) return true;
    if (project.leadArtist === user.shortCode) return true;
    const assignedArtists = (project as any).artists || [];
    return assignedArtists.some((a: any) => (a.artistCode || a.shortCode) === user.shortCode);
  }, [user, isReadOnly, isAdmin, project.leadArtist, (project as any).artists, onDropFiles]);
  
  const isAdminOnlyComms = project.communicationMode === 'admin_only';
  const [commsDialogOpen, setCommsDialogOpen] = useState(false);

  // Soft status tint: reuse the globally-cached /api/status-configs query (no extra fetch)
  // and mix ~10% of the configured project-status color into the row's dark base in JS.
  // blendHex falls back to the dark base if the status hex is missing or malformed.
  // Task #121: admin_only projects use a dimmer base so the greyed-out treatment
  // remains visually distinct from regular open projects.
  const { data: statusConfigs = [] } = useStatusConfigs() as { data: StatusConfig[] };
  const statusColor = useMemo(() => {
    const key = project.projectStatus;
    if (!key) return undefined;
    const cfg = statusConfigs.find(
      c => c.category === 'project' && (c.statusKey === key || c.displayName === key)
    );
    return cfg?.color;
  }, [statusConfigs, project.projectStatus]);
  // Two background values:
  //   baseBg → always painted inline, restores the original dark project-row
  //            tone in BOTH table and timeline modes so non-timeline rows
  //            don't fall back to the page background.
  //   tintedBg → status-color blended into baseBg at 0.18; exposed as the
  //              `--row-status-bg` CSS variable and only painted in timeline
  //              mode (the variable is consumed by an ancestor selector).
  const baseBg = useMemo(
    // Admin-only rows: near-neutral grey at the same perceived brightness as
    // the regular `#2d394b` base (HSL L≈23.5%). A true 0%-saturation grey
    // reads warm/yellow-ish next to the surrounding blue chrome (simultaneous
    // contrast), so we lean it slightly toward the same blue hue (~213°,
    // ~8% sat) to land on a perceived neutral.
    () => isArchived ? '#1a2433' : (isAdminOnlyComms ? '#354049' : '#2d394b'),
    [isArchived, isAdminOnlyComms],
  );
  const tintedBg = useMemo(
    () => blendHex(statusColor, baseBg, 0.18),
    [statusColor, baseBg],
  );

  const hasSubItems = true;
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [tagManagerPosition, setTagManagerPosition] = useState({ x: 0, y: 0 });
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [thumbnailDialogOpen, setThumbnailDialogOpen] = useState(false);

  const dragCounterRef = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragActive(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragActive(false);
    if (!onDropFiles) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onDropFiles(project.id, files);
    }
  }, [onDropFiles, project.id]);

  // Fetch artists from database
  const { data: artists = [] } = useQuery({
    queryKey: ['/api/artists'],
  });

  // Find lead artist data from artists
  const leadArtistData = (artists as any[]).find((artist: any) => artist.shortCode === project.leadArtist);

  const updateProjectMutation = useMutation({
    mutationFn: async (updates: { projectStatus?: string; taskStatus?: string }) => {
      const response = await apiRequest('PATCH', `/api/projects/${project.id}`, updates);
      return response.json();
    },
    onMutate: async (updates) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['/api/projects'] });
      await queryClient.cancelQueries({ queryKey: ['/api/years'] });

      // Snapshot the previous value
      const previousProjects = queryClient.getQueryData(['/api/projects']);
      const previousYears = queryClient.getQueryData(['/api/years']);

      // Optimistically update the project and task status for immediate UI feedback
      if (updates.projectStatus !== undefined || updates.taskStatus !== undefined) {
        // Update standard projects cache
        queryClient.setQueryData(['/api/projects'], (old: any) => {
          if (!old) return old;
          return old.map((p: any) => 
            p.id === project.id 
              ? { 
                  ...p, 
                  ...(updates.projectStatus !== undefined && { projectStatus: updates.projectStatus }),
                  ...(updates.taskStatus !== undefined && { taskStatus: updates.taskStatus })
                }
              : p
          );
        });

        // CRITICAL FIX: Also update paginated projects cache to prevent status reversion
        const paginatedQueries = queryClient.getQueriesData({ 
          queryKey: ['/api/projects', 'paginated'] 
        });
        
        paginatedQueries.forEach(([queryKey, queryData]) => {
          if (queryData && (queryData as any).data) {
            logger.debug('PROJECT_ROW', `Updating paginated projects cache for project ${project.id}`, { queryKey });
            
            const updatedData = {
              ...(queryData as any),
              data: (queryData as any).data.map((p: any) => 
                p.id === project.id 
                  ? { 
                      ...p, 
                      ...(updates.projectStatus !== undefined && { projectStatus: updates.projectStatus }),
                      ...(updates.taskStatus !== undefined && { taskStatus: updates.taskStatus })
                    }
                  : p
              )
            };
            
            queryClient.setQueryData(queryKey, updatedData);
          }
        });

        // Also update in years data if it exists
        queryClient.setQueryData(['/api/years'], (old: any) => {
          if (!old) return old;
          return old.map((year: any) => ({
            ...year,
            projects: year.projects?.map((p: any) => 
              p.id === project.id 
                ? { 
                    ...p, 
                    ...(updates.projectStatus !== undefined && { projectStatus: updates.projectStatus }),
                    ...(updates.taskStatus !== undefined && { taskStatus: updates.taskStatus })
                  }
                : p
            )
          }));
        });

        // CRITICAL FIX: Also update optimized years cache to prevent status reversion  
        queryClient.setQueryData(['/api/years', 'optimized'], (old: any) => {
          if (!old) return old;
          logger.debug('PROJECT_ROW', `Updating optimized years cache for project ${project.id}`);
          return old.map((year: any) => ({
            ...year,
            projects: year.projects?.map((p: any) => 
              p.id === project.id 
                ? { 
                    ...p, 
                    ...(updates.projectStatus !== undefined && { projectStatus: updates.projectStatus }),
                    ...(updates.taskStatus !== undefined && { taskStatus: updates.taskStatus })
                  }
                : p
            )
          }));
        });
      }

      // Return a context object with the snapshotted value
      return { previousProjects, previousYears };
    },
    onError: (err, updates, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousProjects) {
        queryClient.setQueryData(['/api/projects'], context.previousProjects);
        
        // Also rollback paginated projects cache
        const paginatedQueries = queryClient.getQueriesData({ 
          queryKey: ['/api/projects', 'paginated'] 
        });
        paginatedQueries.forEach(([queryKey]) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
      if (context?.previousYears) {
        queryClient.setQueryData(['/api/years'], context.previousYears);
        
        // Also rollback optimized years cache
        queryClient.invalidateQueries({ queryKey: ['/api/years', 'optimized'] });
      }
    },
    onSuccess: () => {
      // Invalidate and refetch to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
    },
  });

  const handleProjectStatusChange = (newStatus: string) => {
    updateProjectMutation.mutate({ projectStatus: newStatus });
  };

  const handleTaskStatusChange = (newStatus: string) => {
    updateProjectMutation.mutate({ taskStatus: newStatus });
  };

  return (
    <div 
      id={`project-row-${project.id}`}
      className={cn(
        "tree-item border-b border-gray-600 relative transition-all",
        isArchived && "opacity-60",
        // Task #121: greyed-out (desaturated) appearance for admin_only projects
        // visible to ALL users — distinct from `opacity-60` archived treatment.
        isAdminOnlyComms && !isArchived && "grayscale-[10%] opacity-90",
        isDragActive && "is-drag-active ring-2 ring-blue-500 bg-blue-500/10 z-50"
      )}
      style={{
        // Always paint the dark base color so non-timeline rows keep their
        // original tone. Timeline mode swaps to the tinted variable via the
        // `.timeline-mode-active` ancestor selector in index.css. Drag-active
        // wins over both via direct inline override.
        backgroundColor: isDragActive ? 'rgba(59, 130, 246, 0.1)' : baseBg,
        ['--row-status-bg' as any]: tintedBg,
        // Exposed so the timeline-mode gradient can paint the future side
        // (right of the today line) back to the neutral base, leaving only
        // the past side tinted. Consumed by the rule in index.css.
        ['--row-base-bg' as any]: baseBg,
      }}
      onDragEnter={canUpload ? handleDragEnter : undefined}
      onDragOver={canUpload ? handleDragOver : undefined}
      onDragLeave={canUpload ? handleDragLeave : undefined}
      onDrop={canUpload ? handleDrop : undefined}
    >
      <div className="grid px-3 items-center gap-0" style={{ gridTemplateColumns: showBudgetColumn ? TREE_GRID_TEMPLATE_WITH_BUDGET : TREE_GRID_TEMPLATE, ...getTreeColumnStyles(showBudgetColumn) }}>
        {/* Thumbnail Column - Retina optimized: 152x75px images displayed at 76x37.5px for crisp 2x rendering */}
        <div 
          className="flex items-center justify-center border-r border-gray-600"
          data-testid="project-thumbnail-cell"
        >
          {!project.thumbnail ? (
            <div
              className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-gray-700/50 transition-colors"
              onClick={() => setThumbnailDialogOpen(true)}
              data-testid={`add-thumbnail-button-${project.id}`}
            >
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Upload className="w-3.5 h-3.5" />
                <span>+ thumb</span>
              </div>
            </div>
          ) : (
            <div className="relative group">
              <img
                src={project.thumbnail}
                alt="Project thumbnail"
                className="w-[76px] h-[37.5px] object-cover rounded"
                data-testid={`project-thumbnail-${project.id}`}
              />
              <button
                onClick={() => setThumbnailDialogOpen(true)}
                className="absolute top-0.5 right-0.5 bg-black/50 rounded p-0.5 opacity-0 group-hover:opacity-100 transition"
              >
                <Settings className="w-3 h-3 text-white" />
              </button>
            </div>
          )}
        </div>

        {/* Expand/Collapse Column - Always show to enable Add pulse */}
        <div className="flex items-center justify-center border-r border-gray-600">
          <IconTooltip label={isExpanded ? "Collapse project" : "Expand project"}>
            <Button
              variant="ghost"
              size="sm"
              className="expandable-row p-0 h-auto"
              onClick={onToggleExpanded}
              aria-label={isExpanded ? "Collapse project" : "Expand project"}
            >
              <ChevronDown
                className={cn(
                  "w-4 h-4 text-gray-400 transition-transform expand-icon",
                  isExpanded ? "expanded" : "collapsed"
                )}
              />
            </Button>
          </IconTooltip>
        </div>
        
        {/* Project ID Column */}
        <div className="flex items-center border-r border-gray-600 px-2">
          <ProjectIdCopyMenu projectId={project.id} projectName={project.projectName} isReadOnly={isReadOnly} />
          {project.sequences && project.sequences.length > 0 && (
            <span className="ml-1 text-sm text-gray-500">≡</span>
          )}
        </div>

        {/* Project Name Column */}
        <div className="min-w-0 px-3 border-r border-gray-600 overflow-hidden group/name">
          <div className="flex items-center gap-1 min-w-0">
            <button
              onClick={() => setDetailDialogOpen(true)}
              className={cn(
                "text-sm truncate hover:text-blue-400 transition-colors text-left flex-1 min-w-0",
                isReadOnly && "text-gray-400"
              )}
              data-testid={`link-project-name-${project.id}`}
            >
              {project.projectName}
            </button>
            {isAdminOnlyComms && isAdmin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCommsDialogOpen(true);
                }}
                className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-medium bg-amber-900/40 text-amber-400 border border-amber-700/40 hover:bg-amber-800/50 transition-colors flex-shrink-0"
                title="Email communications restricted to admins only. Click to configure & open."
                data-testid={`button-open-comms-${project.id}`}
              >
                <Lock className="w-2.5 h-2.5" />
              </button>
            )}
            {!isAdminOnlyComms && isAdmin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCommsDialogOpen(true);
                }}
                className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-medium text-gray-500 hover:text-amber-400 hover:bg-amber-900/20 transition-colors flex-shrink-0 opacity-0 group-hover/name:opacity-100"
                title="Email communications are open. Click to configure or restrict."
                data-testid={`button-comms-settings-${project.id}`}
              >
                <Unlock className="w-2.5 h-2.5" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                setTagManagerPosition({ x: rect.right + 5, y: rect.top });
                setTagManagerOpen(true);
              }}
              className="p-0.5 hover:bg-gray-700 rounded transition-colors"
              title="Manage project tags & dates"
              aria-label="Manage project tags & dates"
              data-tooltip="Manage project tags & dates"
            >
              <TagIcon 
                className="w-3.5 h-3.5 hover:text-gray-300" 
                style={{ 
                  color: (!project.assignedTags || project.assignedTags.length === 0) 
                    ? '#ff8358'  // Orange color like "Stuck" status when no tags
                    : '#6b7280'  // Grey color when tags are assigned
                }}
              />
            </button>
          </div>
          {project.messages && project.messages.length > 0 && (
            <div className="flex items-center mt-1">
              <MessageCircle className="w-3 h-3 text-gray-500 mr-1" />
              <span className="text-xs text-gray-500">
                {project.messages.length} message{project.messages.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Lead Artist Column */}
        <div className="flex items-center px-2">
          {leadArtistData && project.leadArtist && project.leadArtist !== "" && (
            <div className="relative inline-flex">
              <div 
                className="px-1.5 py-0.5 rounded flex items-center justify-center text-xs font-medium text-white min-w-[22px]"
                style={{ backgroundColor: getUserColor(leadArtistData.colorCode) }}
              >
                <span className="text-xs">{leadArtistData.shortCode}</span>
              </div>
            </div>
          )}
        </div>

        {/* Team Column */}
        <div className="flex items-center pl-2">
          <div className="flex flex-col items-start space-y-0.5">
            {project.artists && project.artists.slice(0, 3).map((artist, index) => {
              const artistData = (artists as any[]).find((a: any) => a.shortCode === artist.artistCode);
              return artistData ? (
                <div key={artist.id} className="relative inline-flex">
                  <div
                    className="px-1.5 py-0.5 rounded flex items-center justify-center border border-gray-600 text-xs font-medium text-white min-w-[22px] z-10"
                    style={{ 
                      backgroundColor: getUserColor(artistData.colorCode),
                      zIndex: 10 - index
                    }}
                    title={`${artistData.firstName} ${artistData.lastName} (${artistData.shortCode})`}
                  >
                    <span className="text-xs">{artistData.shortCode}</span>
                  </div>
                </div>
              ) : null;
            })}
            {project.artists && project.artists.length > 3 && (
              <span className="text-xs text-gray-400 ml-1">
                +{project.artists.length - 3}
              </span>
            )}
          </div>
        </div>

        {/* Project Status Column */}
        <div className="px-2">
          <EditableStatus 
            status={project.projectStatus} 
            type="project"
            onStatusChange={handleProjectStatusChange}
          />
        </div>

        {/* Status Column */}
        <div className="px-2">
          <EditableStatus 
            status={project.taskStatus || "working_on_it"}
            type="task"
            onStatusChange={handleTaskStatusChange}
          />
        </div>

        {/* Timing Column - Deadlines/Due Dates */}
        <div className="px-2">
          <TaggableDateCell 
            entityType="project"
            entityId={project.id}
            taggedDates={project.taggedDates || []}
            projectStatus={project.projectStatus}
            taskStatus={project.taskStatus}
          />
        </div>

        {/* Actions Column - hide chat for frozen/archived projects, admins can access settings on deactivated only */}
        <div className="flex items-center justify-end space-x-1">
          {!isReadOnly && <TimeTrackButton projectId={project.id} targetType="project" targetId={project.id} />}
          {/* Edit/Settings button: 
              - Hidden for archived projects (no reactivation possible)
              - Visible for active projects
              - Visible for admins on deactivated projects (can reactivate) */}
          {!isArchived && (!isDeactivated || isAdmin) && (
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white p-1 h-auto"
              title="Edit"
              onClick={() => setEditDialogOpen(true)}
            >
              <Edit className="w-4 h-4" />
            </Button>
          )}
          {/* Chat button: only visible for active projects (write operation) */}
          {!isReadOnly && (
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white p-1 h-auto"
              title="Messages"
              onClick={() => onOpenChat?.(project.id)}
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white p-1 h-auto"
            title="View Files"
            onClick={() => setFileBrowserOpen(true)}
          >
            <Paperclip className="w-4 h-4" />
          </Button>
        </div>

        {showBudgetColumn && (
          <div className="px-2 border-l border-gray-600 overflow-hidden">
            {budgetItems && budgetItems.length > 0 ? (
              <div className="max-h-[60px] overflow-y-auto space-y-0.5 scrollbar-thin">
                {budgetItems.map((item: any, idx: number) => {
                  const status = getActiveStatus(item);
                  return (
                    <div key={item.id || idx} className="flex items-center gap-1 text-xs">
                      <span className="text-gray-300 truncate flex-1 min-w-0" title={item.label}>
                        {item.label}
                      </span>
                      <span className={`px-1 py-0 rounded text-[9px] font-semibold flex-shrink-0 ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="text-xs text-gray-600">—</span>
            )}
          </div>
        )}
      </div>

      <ProjectEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        project={project}
      />

      <ProjectDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        projectId={project.id}
        projectName={project.projectName}
        clientId={project.clientId}
      />
      
      {tagManagerOpen && (
        <TagManager
          projectId={project.id}
          position={tagManagerPosition}
          onClose={() => setTagManagerOpen(false)}
        />
      )}

      {fileBrowserOpen && (
        <FileBrowser
          projectId={project.id}
          onClose={() => setFileBrowserOpen(false)}
        />
      )}

      <ThumbnailUploadDialog
        open={thumbnailDialogOpen}
        onOpenChange={setThumbnailDialogOpen}
        projectId={project.id}
        currentThumbnail={project.thumbnail}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['/api/years'] });
          queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
        }}
      />

      {isAdmin && (
        <PublicModeConfigDialog
          open={commsDialogOpen}
          onOpenChange={setCommsDialogOpen}
          projectId={project.id}
          currentMode={isAdminOnlyComms ? 'admin_only' : 'open'}
          initialEmailCheckBlacklist={!!(project as any).emailCheckBlacklist}
          initialEmailCheckWhitelist={!!(project as any).emailCheckWhitelist}
          initialEmailCheckAi={!!(project as any).emailCheckAi}
          initialEmailCheckAiPrompt={(project as any).emailCheckAiPrompt ?? ""}
        />
      )}
    </div>
  );
}
